import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { EntitlementsProvider } from './contexts/EntitlementsContext';
import { LAScopeProvider, useLAScope } from './contexts/LAScopeContext';
import { ConnectivityBaselineProvider } from './components/ConnectivityProfile';
import { Toaster } from './components/ui/sonner';
import ShareConfirmListener from './components/ShareConfirmListener';
import IdleWarning from './components/IdleWarning';
import Navigation from './components/Navigation';
import LoginPage from './pages/LoginPage';
import HomePage from './pages/HomePage';
import SearchPage from './pages/SearchPage';
import MapView from './pages/MapView';
import BusinessProfile from './pages/BusinessProfile';
import AnalyticsDashboard from './pages/AnalyticsDashboard';
import InterventionPlanner from './pages/InterventionPlanner';
import InterventionMatrix from './pages/InterventionMatrix';
import ChatPage from './pages/ChatPage';
import AdminPanel from './pages/AdminPanel';
import VerificationQueue from './pages/VerificationQueue';
import ConnectivityDashboard from './pages/ConnectivityDashboard';
import PrivacyPolicy from './pages/PrivacyPolicy';
import PublicDashboard from './pages/PublicDashboard';
import AuthorityPage from './pages/AuthorityPage';
import ActivityTracking from './pages/ActivityTracking';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ReportsPage from './pages/ReportsPage';
import PricingPage from './pages/PricingPage';
import AccountPage from './pages/AccountPage';
import AdminPaywallPage from './pages/AdminPaywallPage';
import ClaimPage from './pages/ClaimPage';
import MyBusiness from './pages/MyBusiness';
import AuditPage from './pages/AuditPage';
import PublicProfile from './pages/PublicProfile';
import VerifiedDirectory from './pages/VerifiedDirectory';
import { TermsOfService, CookiePolicy } from './pages/LegalPages';
import { CookieBanner } from './components/CookieBanner';
import { useActivityTracker } from './hooks/useActivityTracker';
import './App.css';

function LoginRedirect() {
  const next = new URLSearchParams(useLocation().search).get('next');
  return <Navigate to={next && next.startsWith('/') ? next : '/dashboard'} replace />;
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-page-bg">
      <div className="text-center">
        <div className="w-8 h-8 rounded-[4px] bg-brand-orange mx-auto mb-3 animate-pulse" />
        <p className="font-body text-sm text-ink/40">Loading...</p>
      </div>
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppLayout({ children }) {
  useActivityTracker();
  const { laScope } = useLAScope();
  return (
    <div className="min-h-screen bg-page-bg">
      <Navigation />
      <div key={laScope}>{children}</div>
    </div>
  );
}

function AppRoutes() {
  const { user, loading } = useAuth();
  
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-page-bg">
      <div className="w-8 h-8 rounded-[4px] bg-brand-orange animate-pulse" />
    </div>
  );

  return (
    <Routes>
      <Route path="/login" element={user ? <LoginRedirect /> : <LoginPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/cookies" element={<CookiePolicy />} />
      {/* Root route: public landing page for guests, logged-in home for authenticated users */}
      <Route path="/" element={user ? <AppLayout><HomePage /></AppLayout> : <PublicDashboard />} />
      <Route path="/public" element={<Navigate to="/" replace />} />
      <Route path="/authority/:slug" element={<AuthorityPage />} />
      <Route path="/claim/:id" element={<ClaimPage />} />
      <Route path="/audit/:id" element={<AuditPage />} />
      <Route path="/b/:id" element={<PublicProfile />} />
      <Route path="/verified" element={<VerifiedDirectory />} />
      <Route path="/my-business" element={<ProtectedRoute><AppLayout><MyBusiness /></AppLayout></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><AppLayout><HomePage /></AppLayout></ProtectedRoute>} />
      <Route path="/search" element={<ProtectedRoute><AppLayout><SearchPage /></AppLayout></ProtectedRoute>} />
      <Route path="/map" element={<ProtectedRoute><AppLayout><MapView /></AppLayout></ProtectedRoute>} />
      <Route path="/business/:id" element={<ProtectedRoute><AppLayout><BusinessProfile /></AppLayout></ProtectedRoute>} />
      <Route path="/analytics" element={<ProtectedRoute><AppLayout><AnalyticsDashboard /></AppLayout></ProtectedRoute>} />
      <Route path="/connectivity" element={<ProtectedRoute><AppLayout><ConnectivityDashboard /></AppLayout></ProtectedRoute>} />
      <Route path="/interventions" element={<ProtectedRoute><AppLayout><InterventionPlanner /></AppLayout></ProtectedRoute>} />
      <Route path="/interventions/matrix" element={<ProtectedRoute><AppLayout><InterventionMatrix /></AppLayout></ProtectedRoute>} />
      <Route path="/chat" element={<ProtectedRoute><AppLayout><ChatPage /></AppLayout></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute><AppLayout><ReportsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/pricing" element={user ? <AppLayout><PricingPage /></AppLayout> : <PricingPage />} />
      <Route path="/account" element={<ProtectedRoute><AppLayout><AccountPage /></AppLayout></ProtectedRoute>} />
      <Route path="/account/commissions" element={<ProtectedRoute><AppLayout><AccountPage /></AppLayout></ProtectedRoute>} />
      <Route path="/admin/paywall" element={<ProtectedRoute><AppLayout><AdminPaywallPage /></AppLayout></ProtectedRoute>} />
      <Route path="/upload" element={<Navigate to="/admin" replace />} />
      <Route path="/admin" element={<ProtectedRoute><AppLayout><AdminPanel /></AppLayout></ProtectedRoute>} />
      <Route path="/admin/verification" element={<ProtectedRoute><AppLayout><VerificationQueue /></AppLayout></ProtectedRoute>} />
      <Route path="/admin/activity" element={<ProtectedRoute><AppLayout><ActivityTracking /></AppLayout></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <LAScopeProvider>
          <EntitlementsProvider>
            <ConnectivityBaselineProvider>
              <AppRoutes />
              <Toaster />
              <ShareConfirmListener />
              <IdleWarning />
              <CookieBanner />
            </ConnectivityBaselineProvider>
          </EntitlementsProvider>
        </LAScopeProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
